import {
  faExclamationCircle as faExclamationCircleRegular,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { snackbarDelay } from 'src/constants/globalConstants';
import { Snackbar as ISnackbar, SnackbarType } from 'src/store/models/Snackbar';
import { colors } from 'src/utils/colors';
import { getTimestamp } from 'src/utils/dateFormatter';
import useMountTransition from 'src/utils/useMountTransition';

import classes from './Snackbar.module.scss';

export interface SnackbarProps {
  snackbar: ISnackbar;
  animationFrom?: 'left' | 'right' | 'none';
  className?: string;
}

const Snackbar: FC<SnackbarProps> = ({
  snackbar,
  animationFrom = 'none',
  className,
}) => {
  const { t } = useTranslation();
  const timer = useRef(null);

  const [open, setOpen] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(snackbarDelay);
  const [pause, setPause] = useState(0);
  const shouldRender = useMountTransition(open, 225);

  useEffect(() => {
    setPause(getTimestamp());
    timer.current = window.setTimeout(() => {
      setOpen(false);
    }, timeRemaining);
    return () => {
      window.clearTimeout(timer.current);
    };
    // eslint-disable-next-line
  }, []);

  const getIcon = () => {
    switch (snackbar.type) {
      case SnackbarType.SUCCESS:
        return faCheckCircle;
      case SnackbarType.INFO:
        return faExclamationCircleRegular;
      case SnackbarType.ERROR:
        return faExclamationCircle;
      default:
        return null;
    }
  };

  const getIconColor = () => {
    switch (snackbar.type) {
      case SnackbarType.SUCCESS:
        return colors()?.colorFumisGreen1;
      case SnackbarType.INFO:
        return colors()?.colorWhite;
      case SnackbarType.ERROR:
        return colors()?.colorRed;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (snackbar.type) {
      case SnackbarType.SUCCESS:
        return t('common:success');
      case SnackbarType.INFO:
        return t('common:note');
      case SnackbarType.ERROR:
        return t('common:fail');
      default:
        return null;
    }
  };

  const getTransition = () => {
    if (open && shouldRender) {
      return { transform: 'translateX(0px)' };
    }
    if (animationFrom === 'left') {
      return { transform: 'translateX(-344px)' };
    } else if (animationFrom === 'right') {
      return { transform: 'translateX(344px)' };
    }
  };

  /* Pause timeout on hover */
  const onMouseEnter = () => {
    window.clearTimeout(timer.current);
    setTimeRemaining((time) => time - (getTimestamp() - pause));
  };

  const onMouseLeave = () => {
    setPause(getTimestamp());
    timer.current = window.setTimeout(() => {
      setOpen(false);
    }, timeRemaining);
  };

  return (
    <>
      {(shouldRender || open) && (
        <div
          style={getTransition()}
          className={classNames(
            classes.Container,
            className,
            classes[snackbar.type],
            {
              [classes.ContainerAnimateIn]: open && shouldRender,
            }
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <FontAwesomeIcon icon={getIcon()} color={getIconColor()} />
          <section>
            <p>{snackbar.title || getTitle()}</p>
            {snackbar.body && <p>{snackbar.body}</p>}
          </section>
          <FontAwesomeIcon icon={faTimes} onClick={() => setOpen(false)} />
        </div>
      )}
    </>
  );
};

export default Snackbar;
